import { useEffect, useState } from 'react';
import QRCode from 'qrcode';

import { PlateChart } from '../PlateChart.jsx';
import { RegisterWidget } from './RegisterWidget.jsx';

/**
 * Renders a login QR widget emitted by the kiosk assistant. The data carries a
 * single-use, short-lived URL the customer scans on their own phone to sign in:
 * nothing is typed on the shared tablet. The URL is rendered as a QR only; it is
 * never shown as clickable text on the kiosk itself.
 */
function LoginQrWidget({ url, expiresAt }) {
  const [dataUrl, setDataUrl] = useState(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    let active = true;
    QRCode.toDataURL(url, { margin: 1, width: 200 })
      .then((d) => { if (active) setDataUrl(d); })
      .catch(() => { if (active) setDataUrl(null); });
    return () => { active = false; };
  }, [url]);

  useEffect(() => {
    if (!expiresAt) return undefined;
    const ts = new Date(expiresAt).getTime();
    const tick = () => setExpired(Date.now() >= ts);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  return (
    <div className="rounded-xl border border-black/10 bg-white p-4 text-ink shadow-sm">
      <p className="mb-2 text-sm font-semibold">Accedi al tuo spazio personale</p>
      {expired ? (
        <p className="text-sm text-ink-muted">Il codice è scaduto. Chiedi all'assistente di generarne uno nuovo.</p>
      ) : (
        <div className="flex items-center gap-4">
          {dataUrl ? (
            <img src={dataUrl} alt="QR per l'accesso" className="h-40 w-40 shrink-0 rounded-lg" />
          ) : (
            <div className="flex h-40 w-40 shrink-0 items-center justify-center rounded-lg bg-black/5 text-xs text-ink-muted">
              QR non disponibile
            </div>
          )}
          <p className="text-sm text-ink-muted">
            Inquadra questo codice con la fotocamera del tuo telefono per entrare nel tuo spazio personale.
            Il codice è personale e scade tra pochi istanti.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Renders the rich widgets attached to an assistant message (e.g. the ORME
 * plate chart returned by the plate tool, or the kiosk login QR). Theme-agnostic:
 * the wrapper uses translucent neutral colours so it sits well on both the
 * admin/seller surface and the warm customer portal. Unknown widget types are
 * ignored.
 */
export function ChatWidgets({ widgets, customerSessionId }) {
  if (!Array.isArray(widgets) || widgets.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-2">
      {widgets.map((w, i) => {
        if (w && w.type === 'login-qr' && w.data && w.data.url) {
          return <LoginQrWidget key={i} url={w.data.url} expiresAt={w.data.expiresAt} />;
        }
        if (w && w.type === 'register-form' && w.data) {
          return <RegisterWidget key={i} prefill={w.data.prefill} customerSessionId={customerSessionId} />;
        }
        if (w && w.type === 'orme-plate' && w.data) {
          return (
            <div
              key={w.data.productId || i}
              className="rounded-xl border border-black/10 bg-white p-3 text-ink shadow-sm"
              style={{ '--plate-hole': '#ffffff' }}
            >
              {w.data.name && <p className="mb-2 text-sm font-semibold">{w.data.name}</p>}
              <PlateChart plate={w.data} dense />
              {Array.isArray(w.data.ingredients) && w.data.ingredients.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {w.data.ingredients.slice(0, 8).map((ing, j) => (
                    <span key={j} className="rounded-full border border-black/10 px-2 py-0.5 text-[11px] opacity-90">
                      {ing.name}{ing.percent != null ? ` ${ing.percent}%` : ''}{ing.allergen ? ' ⚠' : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}
